import { z } from 'zod';
import { AVATAR_PRESET_IDS, NICKNAME_MAX_LENGTH } from '../constants/profile.js';
import { UNPRINTABLE } from './text.js';
/**
 * 利用者のプロフィール（#169、[ADR-025](../../../../docs/adr/025-user-profile-visible-to-friends.md)、
 * `docs/specs/profile.md`）。選手（キャラクター）とは別に、利用者自身が持つ。
 *
 * **見せるのは自分と、ともだちと対戦の相手だけ。** ランキングや大会には出さない（ADR-012 §4）。
 */
export const avatarPresetIdSchema = z.enum(AVATAR_PRESET_IDS);
/** 「えらぶ」で選んだアバター。 */
const presetAvatarSchema = z.object({
    kind: z.literal('preset'),
    presetId: avatarPresetIdSchema,
});
/**
 * アバター。**プリセットか手描きのどちらか 1 つ。**
 *
 * 手描きの画像は R2 の公開 URL（`avatars/<SHA-256>.png`）。内容で決まるキーなので、描き直すと URL も変わる。
 */
export const avatarSchema = z.discriminatedUnion('kind', [
    presetAvatarSchema,
    z.object({
        kind: z.literal('drawn'),
        imageUrl: z.string().url(),
    }),
]);
/**
 * ニックネームの入力。**前後の空白を削ってから** 0〜`NICKNAME_MAX_LENGTH`（コードポイント）。空文字はなし。
 *
 * NG ワードは見ない。見せる相手は、ともだちと対戦の相手（知っている人）だけで、選手の名前も同じ扱い。
 * 知らない相手に見せるときに決める（ADR-025）。
 */
export const nicknameSchema = z
    .string()
    .trim()
    .max(NICKNAME_MAX_LENGTH, `ニックネームは ${NICKNAME_MAX_LENGTH} 文字までにしてください`)
    .refine((value) => !UNPRINTABLE.test(value), {
    message: 'ニックネームに使えない文字が入っています',
});
/**
 * 自分のプロフィール（`GET /api/v1/me` の `profile`、`PATCH /me/profile` と `PUT /me/avatar` の応答）と、
 * ともだちと対戦の相手のプロフィール。
 */
export const profileSchema = z.object({
    /** 空文字はなし。アプリが「なまえ なし」（自分）や「あいて」（相手）に置き換える。 */
    nickname: z.string().max(NICKNAME_MAX_LENGTH),
    /** アバターが無ければ省略（`null` にしない。ほかの省略可能なキーと同じ）。 */
    avatar: avatarSchema.optional(),
});
/**
 * プロフィールを変える（`PATCH /api/v1/me/profile`）。**どちらも省略できる**（省略したものは変えない）。
 *
 * 手描きのアバターはここでは受け取らない。画像は `PUT /api/v1/me/avatar` に PNG のまま送る
 * （base64 の JSON にすると 33% 膨らむ）。アバターを無しに戻す手段は無い（デザインに無い）。
 */
export const updateProfileSchema = z.object({
    nickname: nicknameSchema.optional(),
    avatar: presetAvatarSchema.optional(),
});
